import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toggleLayerOnOff, updateLayerColor, updateLayerProperty } from '../../../redux/layerSlice';
import ColorSelect from '../../common/ColorSelect/ColorSelect';
import { getIcon } from '../../../utils/icons';
import './LayerSettingsDialog.css';

const LINEWEIGHT_OPTIONS = [
  { value: -3, label: 'Default' },
  { value: 0, label: '0.00 mm' },
  { value: 5, label: '0.05 mm' },
  { value: 9, label: '0.09 mm' },
  { value: 13, label: '0.13 mm' },
  { value: 15, label: '0.15 mm' },
  { value: 18, label: '0.18 mm' },
  { value: 20, label: '0.20 mm' },
  { value: 25, label: '0.25 mm' },
  { value: 30, label: '0.30 mm' },
  { value: 35, label: '0.35 mm' },
  { value: 40, label: '0.40 mm' },
  { value: 50, label: '0.50 mm' },
  { value: 53, label: '0.53 mm' },
  { value: 60, label: '0.60 mm' },
  { value: 70, label: '0.70 mm' },
  { value: 80, label: '0.80 mm' },
  { value: 90, label: '0.90 mm' },
  { value: 100, label: '1.00 mm' },
  { value: 106, label: '1.06 mm' },
  { value: 120, label: '1.20 mm' },
  { value: 140, label: '1.40 mm' },
  { value: 158, label: '1.58 mm' },
  { value: 200, label: '2.00 mm' },
  { value: 211, label: '2.11 mm' },
];

const LINETYPE_OPTIONS = [
  'Continuous',
  'DASHED',
  'DASHDOT',
  'DOT',
  'CENTER',
  'HIDDEN',
  'PHANTOM',
  'BORDER',
  'DIVIDE',
];

// Lock icons are now inline SVG via getIcon("locked"/"unlocked")

function colorToHex(color) {
  if (typeof color === 'string' && color.startsWith('#')) return color;
  if (typeof color === 'number') return '#' + color.toString(16).padStart(6, '0');
  return '#ffffff';
}

export default function LayerSettingsDialog({ isOpen, onClose }) {
  const layers = useSelector(state => state.layer.allLayers || []);
  const activeLayerName = useSelector(state => state.layer.activeLayerName);
  const dispatch = useDispatch();

  const [colorSelectOpen, setColorSelectOpen] = useState(false);
  const [colorSelectLayer, setColorSelectLayer] = useState(null);

  if (!isOpen) return null;

  const handleToggleOnOff = (layerName) => {
    dispatch(toggleLayerOnOff(layerName));
  };

  const handleToggleLock = (layer) => {
    dispatch(updateLayerProperty({
      layerName: layer.name,
      property: 'locked',
      value: !layer.locked,
    }));
  };

  const handleToggleFreeze = (layer) => {
    dispatch(updateLayerProperty({
      layerName: layer.name,
      property: 'frozen',
      value: !layer.frozen,
    }));
  };

  const handleColorClick = (layerName) => {
    setColorSelectLayer(layerName);
    setColorSelectOpen(true);
  };

  const handleColorSelect = (aci, hex) => {
    if (colorSelectLayer) {
      dispatch(updateLayerColor({ layerName: colorSelectLayer, color: hex }));
    }
  };

  const handleLineweightChange = (layerName, value) => {
    dispatch(updateLayerProperty({
      layerName,
      property: 'lineWeight',
      value: parseInt(value),
    }));
  };

  const handleLinetypeChange = (layerName, value) => {
    dispatch(updateLayerProperty({
      layerName,
      property: 'lineType',
      value,
    }));
  };

  const handleTransparencyChange = (layerName, value) => {
    dispatch(updateLayerProperty({
      layerName,
      property: 'transparency',
      value: parseInt(value),
    }));
  };

  return (
    <div className="ls-overlay" onClick={onClose}>
      <div className="ls-dialog" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="ls-header">
          <span className="ls-title">Katman Ayarlari</span>
          <button className="ls-close" onClick={onClose}>&times;</button>
        </div>

        {/* Body */}
        <div className="ls-body">
          <table className="ls-table">
            <thead>
              <tr>
                <th>Durum</th>
                <th>Ad</th>
                <th>Renk</th>
                <th>Cizgi Tipi</th>
                <th>Kalinlik</th>
                <th>Seffaflik</th>
              </tr>
            </thead>
            <tbody>
              {layers.map(layer => {
                const isOn = layer.onOff !== false;
                const isLocked = layer.locked === true;
                const isFrozen = layer.frozen === true;
                const hex = colorToHex(layer.color || layer.trueColor || layer.rgb || layer.acadColor || 7);
                const isActive = layer.name === activeLayerName;

                return (
                  <tr key={layer.name} className={isActive ? 'ls-active-row' : ''}>
                    {/* Status: on/off, freeze, lock */}
                    <td>
                      <div className="ls-status-cell">
                        <button
                          className="ls-icon-btn"
                          title={isOn ? 'Acik — Kapatmak icin tikla' : 'Kapali — Acmak icin tikla'}
                          style={{ opacity: isOn ? 1 : 0.4 }}
                          onClick={() => handleToggleOnOff(layer.name)}
                        >💡</button>
                        <button
                          className="ls-icon-btn"
                          title={isFrozen ? 'Donmus — Cozmek icin tikla' : 'Aktif — Dondurmak icin tikla'}
                          style={{ color: isFrozen ? '#00eaff' : '#888', filter: isFrozen ? 'none' : 'grayscale(1) opacity(0.6)' }}
                          onClick={() => handleToggleFreeze(layer)}
                        >❄️</button>
                        <button
                          className="ls-icon-btn"
                          title={isLocked ? 'Kilitli — Acmak icin tikla' : 'Acik — Kilitlemek icin tikla'}
                          onClick={() => handleToggleLock(layer)}
                        >
                          {getIcon(isLocked ? "locked" : "unlocked", 16)}
                        </button>
                      </div>
                    </td>

                    {/* Name */}
                    <td>
                      <span className="ls-layer-name" title={layer.name}>{layer.name}</span>
                    </td>

                    {/* Color */}
                    <td>
                      <span
                        className="ls-color-swatch"
                        style={{ background: hex }}
                        title={`Renk: ${hex}`}
                        onClick={() => handleColorClick(layer.name)}
                      />
                    </td>

                    {/* Line Type */}
                    <td>
                      <select
                        className="ls-select"
                        value={layer.lineType || 'Continuous'}
                        onChange={(e) => handleLinetypeChange(layer.name, e.target.value)}
                      >
                        {LINETYPE_OPTIONS.map(lt => (
                          <option key={lt} value={lt}>{lt}</option>
                        ))}
                        {/* Eger mevcut deger listede yoksa ekle */}
                        {layer.lineType && !LINETYPE_OPTIONS.includes(layer.lineType) && (
                          <option value={layer.lineType}>{layer.lineType}</option>
                        )}
                      </select>
                    </td>

                    {/* Lineweight */}
                    <td>
                      <select
                        className="ls-select"
                        value={layer.lineWeight ?? -3}
                        onChange={(e) => handleLineweightChange(layer.name, e.target.value)}
                      >
                        {LINEWEIGHT_OPTIONS.map(lw => (
                          <option key={lw.value} value={lw.value}>{lw.label}</option>
                        ))}
                      </select>
                    </td>

                    {/* Transparency */}
                    <td>
                      <div className="ls-transparency">
                        <input
                          type="range"
                          min="0"
                          max="90"
                          value={layer.transparency || 0}
                          onChange={(e) => handleTransparencyChange(layer.name, e.target.value)}
                        />
                        <span className="ls-transparency-val">{layer.transparency || 0}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="ls-footer">
          <button className="ls-btn ls-btn-ok" onClick={onClose}>Tamam</button>
        </div>

        {/* Color Select */}
        <ColorSelect
          isOpen={colorSelectOpen}
          onClose={() => setColorSelectOpen(false)}
          onSelect={handleColorSelect}
          initialAci={7}
        />
      </div>
    </div>
  );
}
